//#region ENUM RuleHumanIdEnum
/**
 * ### RuleHumanIdEnum
 * Enum representing human-readable rule identifiers used to uniquely identify rules in the system.
 *
 * @enum {string}
 * @description This enum contains string constants for each rule identifier. These identifiers are used
 *              to refer to individual rules within the system, such as permissions or access control rules.
 *
 * @example
 * const ruleId: RuleHumanIdEnum = RuleHumanIdEnum['a.'];
 */
export enum RuleHumanIdEnum {
  /** Edit Staff Access */
    'a.' = 'a.',
  /** View / Search Stock Items */
    'b.' = 'b.',
  /** Access Stock Edit Interface */
    'b.i' = 'b.i',
  /** Submit Stock Edits to IQ */
    'b.ii' = 'b.ii',
  /** View NGP Report */
    'c.' = 'c.',
  /** Access Auto Ordering Tool */
    'd.' = 'd.',
  /**
   * See Colleagues Orders
   * @deprecated Legacy — not assignable via Customise Permissions UI
   */
    'd.i' = 'd.i',
  /**
   * Edit Colleagues Orders
   * @deprecated Legacy — not assignable via Customise Permissions UI
   */
    'd.ii' = 'd.ii',
  /** Submit Auto Orders */
    'd.iii' = 'd.iii',
  /** Edit Schedule */
    'd.iv' = 'd.iv',
  /** Run Unscheduled Orders */
    'd.v' = 'd.v',
  /** View & Edit Settings */
    'd.vi' = 'd.vi',
  /** View Dashboard */
    'e.' = 'e.',
  /** View Shelf Talkers */
    'f.' = 'f.',
  /** Barcode Scanner (also allows updating stock `_tags` only) */
    'g.' = 'g.',
  /** Stock take - Can count / edit stock takes (not process or delete) */
    'i.' = 'i.',
  /** Stock take - Can process stock takes on server, delete stock takes, edit schedule */
    'j.' = 'j.',

}

//#endregion

//#region TYPE RuleHumanID
/**
 * ### RuleHumanID
 * Type representing all possible human-readable rule IDs.
 *
 * @type {RuleHumanID}
 * @description This type ensures consistency across the application by using string values derived
 *              directly from `RuleHumanIdEnum` to represent rule identifiers.
 *
 * @example 'a.'
 */
export type RuleHumanID = `${RuleHumanIdEnum}`;
//#endregion

//#region ENUM RuleIdCodeEnum
/**
 * ### RuleIdCodeEnum
 * Enum representing unique numeric identifiers for each rule.
 *
 * @enum {number}
 * @description This enum maps numeric rule codes to human-readable rule identifiers. It is used for
 *              mapping indices to specific rules in the `rawRuleStructure` array.
 *
 * @example
 * const ruleCode: RuleIdCodeEnum = RuleIdCodeEnum['a.'];
 */
export enum RuleIdCodeEnum {
  /** Edit Staff Access */
    'a.' = 0,
  /** View / Search Stock Items */
    'b.' = 1,
  /** Access Stock Edit Interface */
    'b.i' = 2,
  /** Submit Stock Edits to IQ */
    'b.ii' = 3,
  /** View NGP Report */
    'c.' = 4,
  /** Access Auto Ordering Tool */
    'd.' = 5,
  /**
   * See Colleagues Orders
   * @deprecated Legacy — not assignable via Customise Permissions UI
   */
    'd.i' = 6,
  /**
   * Edit Colleagues Orders
   * @deprecated Legacy — not assignable via Customise Permissions UI
   */
    'd.ii' = 7,
  /** Submit Auto Orders */
    'd.iii' = 8,
  /** Edit Schedule */
    'd.iv' = 9,
  /** Run Unscheduled Orders */
    'd.v' = 10,
  /** View & Edit Settings */
    'd.vi' = 11,
  /** View Dashboard */
    'e.' = 12,
  /** View Shelf Talkers */
    'f.' = 13,
  /** Barcode Scanner (also allows updating stock `_tags` only) */
    'g.' = 14,
  /** Stock take - Can count / edit stock takes (not process or delete) */
    'i.' = 16,
  /** Stock take - Can process stock takes on server, delete stock takes, edit schedule */
    'j.' = 17,
}

//#endregion

//#region CONST RuleIdCodeEnumReverse
/**
 * ### RuleIdCodeEnumReverse
 * Reverse mapping of `RuleIdCodeEnum` from numeric codes to human-readable rule identifiers.
 *
 * @constant
 * @type {Object.<number, keyof typeof RuleIdCodeEnum>}
 * @description This constant provides the reverse mapping, allowing for lookup of the human-readable
 *              rule ID based on its numeric code.
 *
 * @example
 * const ruleId: keyof typeof RuleIdCodeEnum = RuleIdCodeEnumReverse[0];
 */
export const RuleIdCodeEnumReverse: Record<number, keyof typeof RuleIdCodeEnum> = Object
  .entries(RuleIdCodeEnum)
  .filter(([key, value]) => typeof value === 'number')
  .reduce((acc, [key, value]) => {
    acc[value] = key;
    return acc;
  }, {} as Record<number, keyof typeof RuleIdCodeEnum>);

//#endregion
